import { z } from 'zod';

export enum Rating {
  Neutral = 'neutral',
  Positive = 'positive',
  Negative = 'negative',
}

export enum FeedbackCategory {
  IrrelevantInformation = 'irrelevantInformation',
  MissingInformation = 'missingInformation',
  InsufficientDetail = 'insufficientDetail',
  IncorrectInformation = 'incorrectInformation',
  Other = 'other',
}

export enum ChatRole {
  User = 'user',
  Administrator = 'administrator',
  Assistant = 'assistant',
  Status = 'status',
}

export enum Role {
  Admin = 'admin',
  SuperAdmin = 'superAdmin',
}

export enum RoleWOSuperAdmin {
  Admin = 'admin',
}

export enum Agent {
  FormFactory = 'formFactory',
}

/**
 * OpenAI: https://platform.openai.com/docs/models/gpt-4o
 * Anthropic: https://docs.anthropic.com/en/docs/about-claude/models#model-comparison-table
 * openrouter.ai: https://openrouter.ai/
 */
export enum ChatModel {
  // Anthropic models
  Claude35Sonnet = 'claude-3-5-sonnet-latest',

  // OpenAI models
  Gpt41 = 'gpt-4.1',
  Gpt41Mini = 'gpt-4.1-mini',
  Gpt41Nano = 'gpt-4.1-nano',
  Gpt5 = 'gpt-5',
  Gpt5Mini = 'gpt-5-mini',
  Gpt5Nano = 'gpt-5-nano',
  GptO3Mini = 'o3-mini-2025-01-31',

  // OpenRouter.ai models
  Grok4Fast = 'x-ai/grok-4-fast',
  Grok4 = 'x-ai/grok-4',
  Gemini2Flash = 'google/gemini-2.0-flash-001',
  Gemini2FlashLite = 'google/gemini-2.0-flash-lite-001',
  Gemini25Flash = 'google/gemini-2.5-flash',
  Gemini25FlashLite = 'google/gemini-2.5-flash-lite',
  Gemini25Pro = 'google/gemini-2.5-pro',
  ClaudeHaiku45 = 'anthropic/claude-haiku-4.5',
  GLM45 = 'z-ai/glm-4.5',
  OPENROUTER_AUTO = 'openrouter/auto',
}

/**
 * Embedding models
 */
export enum EmbeddingModel {
  // OpenAI models
  TextEmbeddingAda002 = 'text-embedding-ada-002',
  TextEmbedding3Small = 'text-embedding-3-small',
  TextEmbedding3Large = 'text-embedding-3-large',

  // JinaAI models
  JinaEmbeddingsV3 = 'jina-embeddings-v3',
}

export enum ContentType {
  Website = 'website',
  Document = 'document',
  ExternalDocument = 'externalDocument',
}

export enum ToolType {
  Text = 'text',
  Tool = 'tool',
}

export enum TonePreset {
  Friendly = 'friendly',
  Casual = 'casual',
  Balanced = 'balanced',
  Professional = 'professional',
  Formal = 'formal',
}

export enum ResponseLengthPreset {
  VeryShort = 'very_short',
  Short = 'short',
  Medium = 'medium',
  Long = 'long',
  VeryLong = 'very_long',
}

export enum Language {
  Czech = 'cs',
  English = 'en',
  Slovak = 'sk',
  Croatian = 'hr',
}

export enum EFitnessGender {
  WOMAN,
  MAN,
  OTHER,
}

export * from './eFitnessEnums';

export enum DocumentStatus {
  ParsingError = 'parsingError',
  EmbeddingError = 'embeddingError',
  Uploading = 'uploading',
  Ready = 'ready',
  Parsing = 'parsing',
  ParsingAsync = 'parsingAsync', // Represents a job that is scheduled but not yet completed
  Parsed = 'parsed',
  Embedding = 'embedding',
  Done = 'done',
}

export enum JobProvider {
  LlamaParse = 'llamaparse',
  Firecrawl = 'firecrawl',
  Mistral = 'mistral',
}

/**
 * Categories of usage records.
 */
// How about more categories? (Voice/Chat instead of agent)
export enum RecordCategory {
  Data = 'data',
  Agent = 'agent',
  Other = 'other',
  RAG = 'rag',
}

/**
 * Types of usage records. This identifies the actual operation that was performed.
 * Llm for LLM calls, Embedding for embedding calls, Parsing for parsing calls, etc.
 */
export enum RecordType {
  // All calls to the LLMs (agent nodes, chat calls, etc.)
  Llm = 'llm',

  // All calls to the TTS models
  TTS = 'tts',

  // All calls to the STT models
  STT = 'stt',

  // All calls to the embedding models
  Embedding = 'embedding',

  // All calls to the parsing models/services/tools
  Parsing = 'parsing',

  // All calls to the parsing models/services/tools
  Scraping = 'scraping',

  // All calls to the reranking models
  Rerank = 'rerank',

  // All other types
  Other = 'other',
}

/**
 * Types of units that can be used to track usage.
 */
export enum RecordUnit {
  Tokens = 'tokens',
  Characters = 'characters',
  Seconds = 'seconds',
  Credits = 'credits',
}

/**
 * Providers of the models/services/tools.
 */
export enum Provider {
  OpenAI = 'openai',
  OpenRouter = 'openrouter',
  Cohere = 'cohere',
  Anthropic = 'anthropic',
  Firecrawl = 'firecrawl',
  LlamaParse = 'llamaparse',
  JinaAI = 'jinaai',
  ElevenLabs = 'elevenlabs',
  Gladia = 'gladia',
  Deepgram = 'deepgram',
  Mistral = 'mistral',
}

/**
 * Subset of providers that are STT providers.
 */
export enum STTProvider {
  Gladia = Provider.Gladia,
  Deepgram = Provider.Deepgram,
}

/**
 * Subset of providers that are TTS providers.
 */
export enum TTSProvider {
  ElevenLabs = Provider.ElevenLabs,
}

/**
 * Modes of the thread. Either user talks to human or AI.
 */
export enum ThreadMode {
  Human = 'human',
  AI = 'ai',
}

/**
 * Categories of the thread. Either chat.
 */
export enum ThreadCategory {
  Chat = 'chat',
}

/**
 * Names of the entities that can be associated with usage records.
 */
export enum EntityType {
  Document = 'document',
  ChatMessage = 'chatMessage',
  Thread = 'thread',
  Chat = 'chat',
}

export enum ChatPlacementPosition {
  Left = 'left',
  Right = 'right',
}

export enum Gender {
  None = 'none',
  Male = 'male',
  Female = 'female',
}

/**
 * User gender options for membership profiles
 */
export enum UserGender {
  Man = 'man',
  Woman = 'woman',
  Other = 'other',
}

/**
 * Membership intent classification types
 */
export enum MembershipIntent {
  Profile = 'profile',
  Registration = 'registration',
  Pause = 'pause',
  Status = 'status',
  Other = 'other',
}

/**
 * Membership tool response types
 */
export enum MembershipResponseType {
  MembershipStatus = 'membership_status',
  UserProfile = 'user_profile',
  MembershipRegistration = 'membership_registration',
  MembershipLogin = 'membership_login',
  Other = 'other',
}

export enum ThemePreset {
  Light = 'light',
  Dark = 'dark',
}

export enum SourcesFormat {
  Simple = 'simple',
  Academic = 'academical',
}

export enum SubscriptionTier {
  Free = 'Free',
  Basic = 'Basic',
  Standard = 'Standard',
  Premium = 'Premium',
  Enterprise = 'Enterprise',
}

/**
 * Subscription Statuses aligned with Stripe.
 * See: https://stripe.com/docs/api/subscriptions/object#subscription_object-status
 */
export enum SubscriptionStatus {
  Active = 'active',
  Canceled = 'canceled',
  Incomplete = 'incomplete',
  IncompleteExpired = 'incomplete_expired',
  PastDue = 'past_due',
  Trialing = 'trialing',
  Unpaid = 'unpaid',
  // 'paused' is another potential Stripe status, but often handled application-side.
}

export enum BillingInterval {
  Month = 'month',
  Year = 'year',
}

export const subscriptionStatusEnum = z.nativeEnum(SubscriptionStatus);
export const subscriptionTierEnum = z.nativeEnum(SubscriptionTier);
export const billingIntervalEnum = z.nativeEnum(BillingInterval);

export enum OmnichannelPostbackAction {
  AcceptTakeover = 'accept_takeover',
  DeclineTakeover = 'decline_takeover',
}

export enum Currency {
  USD = 'usd',
  CZK = 'czk',
}

export enum ChargeType {
  BalanceTopUp = 'balance_top_up',
  Subscription = 'subscription',
  AutoRenewal = 'auto_renewal',
}
